// @ts-check
import { ArrowDown, ArrowUp, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { adminProductSimilarPinsService } from '../services/adminProductExtrasService';
import adminProductService from '../services/adminProductService';

const ProductSimilarPinsManager = ({ productId }) => {
  const toast = useToast();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);

  const loadPins = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminProductSimilarPinsService.getSimilarPins(productId);
      setPins(data);
    } catch (err) {
      toast.error('Failed to load pinned products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId, toast]);

  useEffect(() => {
    loadPins();
  }, [loadPins]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Debounced search — excludes this product itself and anything already pinned. Runs as
  // soon as the panel opens (browsing the default top products) and re-filters on every
  // keystroke, rather than showing nothing until the admin types something.
  useEffect(() => {
    if (!showSearch) return;

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const query = searchQuery.trim();
        const data = await adminProductService.getAllProducts(query ? { search: query, limit: 10 } : { limit: 10 });
        const items = data.items || data;
        const excludeIds = new Set([productId, ...pins.map((p) => p.id)]);
        setSearchResults(Array.isArray(items) ? items.filter((p) => !excludeIds.has(String(p.id))) : []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, showSearch, pins, productId]);

  const handleAdd = async (product) => {
    try {
      await adminProductSimilarPinsService.addSimilarPin(productId, product.id);
      toast.success(`Pinned "${product.name}" to More Like This`);
      // Keep the query and results as-is — refreshing `pins` re-runs the search effect
      // above and it naturally drops the just-added product via excludeIds, instead of
      // wiping the whole result list and forcing the admin to retype to pin another.
      await loadPins();
    } catch (err) {
      toast.error(err.message || 'Failed to pin product');
      console.error(err);
    }
  };

  const handleRemove = async (similarProductId) => {
    const pin = pins.find((p) => p.id === similarProductId);
    if (!pin) return;

    const prev = [...pins];
    setPins(pins.filter((p) => p.id !== similarProductId));

    try {
      await adminProductSimilarPinsService.deleteSimilarPin(productId, similarProductId);
      toast.success(`Unpinned "${pin.name}" from More Like This`);
    } catch (err) {
      toast.error('Failed to unpin product');
      console.error(err);
      setPins(prev);
    }
  };

  const handleReorder = async (similarProductId, direction) => {
    const idx = pins.findIndex((p) => p.id === similarProductId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= pins.length) return;

    const updated = [...pins];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setPins(updated);

    try {
      await adminProductSimilarPinsService.reorderSimilarPins(
        productId,
        updated.map((p) => p.id),
      );
    } catch (err) {
      toast.error('Failed to reorder pinned products');
      console.error(err);
      loadPins();
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading pinned products...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground/70">
          Shown first, in this order, in the "More Like This" section on the product page — remaining slots are filled
          automatically by similarity (type, collection, tags, price). Turn the section off entirely with "Show 'More
          Like This' on product page" on the Basic Information tab.
        </p>
        <button
          type="button"
          onClick={() => {
            setShowSearch(!showSearch);
            setSearchQuery('');
            setSearchResults([]);
          }}
          className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs hover:bg-primary/90 transition flex-shrink-0"
        >
          <Plus size={14} />
          Pin Product
        </button>
      </div>

      {showSearch && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name..."
              className="w-full pl-9 pr-9 py-2 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-foreground"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {searching && <p className="text-xs text-muted-foreground/70">Searching...</p>}

          {searchResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {searchResults.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  onClick={() => handleAdd(product)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition text-left"
                >
                  <div className="flex-shrink-0 w-10 h-13 bg-muted rounded-lg overflow-hidden">
                    {product.image_urls?.[0] ? (
                      <img src={product.image_urls[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-[9px]">
                        No img
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      ₹{product.price}
                      {product.status !== 'published' && (
                        <span className="ml-2 text-amber-500 uppercase text-[10px] font-medium">{product.status}</span>
                      )}
                    </p>
                  </div>
                  <Plus size={16} className="flex-shrink-0 text-muted-foreground/70" />
                </button>
              ))}
            </div>
          )}

          {!searching && searchResults.length === 0 && (
            <p className="text-xs text-muted-foreground/70 text-center py-2">
              {searchQuery.trim() ? 'No matching products found.' : 'No products available to pin.'}
            </p>
          )}
        </div>
      )}

      {pins.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No products pinned yet — "More Like This" will show only automatic recommendations. Click "Pin Product" to
          guarantee specific products appear first.
        </div>
      ) : (
        <div className="space-y-2">
          {pins.map((pin, index) => (
            <div key={pin.id} className="flex items-center gap-4 p-3 border border-border rounded-lg bg-card">
              <div className="flex-shrink-0 w-12 h-16 bg-muted rounded-lg overflow-hidden">
                {pin.image_url ? (
                  <img src={pin.image_url} alt={pin.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-xs">
                    No img
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{pin.name}</p>
                <p className="text-xs text-muted-foreground">
                  ₹{pin.price}
                  {pin.status !== 'published' && (
                    <span className="ml-2 text-amber-500 uppercase text-[10px] font-medium">
                      {pin.status} — hidden from customers
                    </span>
                  )}
                  {pin.status === 'published' && pin.out_of_stock && (
                    <span className="ml-2 text-amber-500 uppercase text-[10px] font-medium">
                      Out of stock — hidden from customers
                    </span>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => handleReorder(pin.id, 'up')}
                    disabled={index === 0}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReorder(pin.id, 'down')}
                    disabled={index === pins.length - 1}
                    className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(pin.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                  title="Unpin from More Like This"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductSimilarPinsManager;
